import React, { useState } from 'react';
import { font, radius, space } from '../../theme';
import Button, { ButtonLink } from '../Button';
import PlayerDialog from '../PlayerDialog';
import { canPlayInApp, LiveScheduleSession, OnAirCard } from '../../utils/live';

/* The watch buttons of the live cards and banners (decision 1A) and of past session cards
   (decision 6A). A stream that plays here (utils/live canPlayInApp) opens the in-app player
   (components/PlayerDialog: a bottom sheet on phone, a dialog on desktop); anything else
   links out to the session's page on the Knesset site. */

// The mockups' small button, as tall as a ButtonLink, so both sit on one row
export const SMALL_BUTTON: React.CSSProperties = {
  minHeight: 36, padding: `0 ${space.md + 2}px`, borderRadius: radius.md, fontSize: font.small,
};

// 'HH:MM' of an Israel-local 'YYYY-MM-DDTHH:MM:SS' (no time zone math: it's already local)
export const hhmm = (dt: string | null | undefined): string => (dt && dt.length >= 16 ? dt.slice(11, 16) : '');

const https = (url: string): string => url.replace(/^http:/, 'https:');

type WithBroadcast = LiveScheduleSession & { broadcastUrl?: string | null };

// Where to watch on the Knesset site: the schedule's broadcast link, else the session's page
export const externalWatchUrl = (card: OnAirCard<WithBroadcast>): string | null => {
  const url = (card.session && card.session.broadcastUrl) || card.sessionUrl;
  return url ? https(url) : null;
};

interface WatchLiveButtonProps<S extends WithBroadcast> {
  card: OnAirCard<S>;
  label?: string;
  // The player's "על סדר היום" when the feed has no subject (e.g. the schedule's first item)
  agenda?: string | null;
}

// "צפייה": the in-app player for a playable stream; else "צפייה באתר הכנסת" (link out); else nothing
export const WatchLiveButton = <S extends WithBroadcast>({ card, label = 'צפייה', agenda }: WatchLiveButtonProps<S>) => {
  const [open, setOpen] = useState(false);
  const since = hhmm(card.since);
  if (card.streamUrl && canPlayInApp(card.streamUrl)) {
    return (
      <>
        <Button variant={card.paused ? 'secondary' : 'primary'} icon="play" onClick={() => setOpen(true)} style={SMALL_BUTTON}>{label}</Button>
        <PlayerDialog
          open={open}
          onClose={() => setOpen(false)}
          src={card.streamUrl}
          title={card.title}
          subtitle={since ? `${card.paused ? 'מתקיים ' : ''}מאז ${since}` : null}
          live
          paused={card.paused}
          poster={card.posterUrl}
          speaker={card.speakers.length > 0 ? card.speakers[0] : null}
          agenda={card.subject || agenda || null}
          externalUrl={card.sessionUrl ? https(card.sessionUrl) : null}
        />
      </>
    );
  }
  const href = externalWatchUrl(card);
  return href ? <ButtonLink href={href} icon="external">צפייה באתר הכנסת</ButtonLink> : null;
};

interface RecordingButtonProps {
  src: string;              // a recording that canPlayInApp() accepts
  title: string;
  subtitle?: string | null; // e.g. the session's date
  agenda?: string | null;
  externalUrl?: string | null;
}

// "צפייה בהקלטה": a session's recording in the in-app player (callers check canPlayInApp first)
export const RecordingButton = ({ src, title, subtitle, agenda, externalUrl }: RecordingButtonProps) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button icon="video" onClick={() => setOpen(true)} style={SMALL_BUTTON}>צפייה בהקלטה</Button>
      <PlayerDialog
        open={open}
        onClose={() => setOpen(false)}
        src={src}
        title={title}
        subtitle={subtitle}
        agenda={agenda}
        externalUrl={externalUrl ? https(externalUrl) : null}
      />
    </>
  );
};
